/**
 * The two things a till needs to SELL, kept on the device.
 *
 * The credential store above it answers "who may sign in". This answers "what
 * may they ring, and where are the tables" — and without it a till reloaded
 * during an outage boots, lets somebody sign in, and can do nothing at all: no
 * floor plan, no menu, an empty screen with a banner saying selling continues.
 *
 * Found by cutting the wifi in a browser and pressing reload. Every warning in
 * the log said "keeping the cached copy"; there was no cached copy. The cache
 * was a variable in a module that a reload had just discarded.
 *
 * SAME OPFS DATABASE as the outbox, the drawer journal, the leases and the
 * credentials. One database to open, one set of durability settings, nothing
 * second that can be half-migrated.
 *
 * WHAT IS STORED IS NOT SECRET AND NOT AUTHORITATIVE. The config bundle is
 * signed by the server and is re-verified on the way out exactly as it is when
 * it arrives over the network — a bundle edited on disk verifies against
 * nothing. The menu is a published version and a price list; it is on a
 * chalkboard.
 *
 * BOTH CARRY THEIR VERSION, so a till that reconnects can tell whether what it
 * is holding is still current rather than trusting age.
 */

import type { SqliteDriver } from "@gf/kernel/sqlite";
import { decodePayload, encodePayload } from "@gf/kernel/json";

interface Row {
  readonly payload_json: string;
  readonly version: number;
}

/**
 * One row per kind, keyed by a name.
 *
 * A table per kind would be three migrations and three stores for one shape.
 * The key is a closed set and a typo is a miss, which is the same outcome as an
 * empty cache and therefore safe.
 *
 * `open-checks` is the floor the server last showed this till: the tables OTHER
 * devices have open. Without it, a till reloaded during an outage came back
 * believing the restaurant was empty except for its own tables, and seating
 * somebody at table 1 opened a SECOND check on a table that already had a bill
 * on it. The local journal beside this one cannot help — this till never rang
 * those rounds, so it has no events of its own to fold.
 */
export type CachedKind = "menu" | "config" | "open-checks";

export class SqliteOfflineCache {
  readonly #driver: SqliteDriver;

  constructor(driver: SqliteDriver) {
    this.#driver = driver;
  }

  /** Idempotent: every app start runs it. */
  async migrate(): Promise<void> {
    await this.#driver.exec(`
      CREATE TABLE IF NOT EXISTS offline_cache (
        kind         TEXT    NOT NULL PRIMARY KEY,
        payload_json TEXT    NOT NULL,
        version      INTEGER NOT NULL,
        stored_at    INTEGER NOT NULL
      )
    `);
  }

  /**
   * Replace what is held for this kind.
   *
   * REPLACE rather than keep a history. There is exactly one current menu and
   * one current configuration; a till holding two would have to choose, and the
   * only defensible choice is the newest, which is the one this keeps.
   */
  async put(kind: CachedKind, payload: unknown, version: number, at: Date): Promise<void> {
    await this.#driver.run(
      `INSERT INTO offline_cache (kind, payload_json, version, stored_at)
       VALUES (?, ?, ?, ?)
       ON CONFLICT (kind) DO UPDATE SET payload_json = excluded.payload_json,
                                        version      = excluded.version,
                                        stored_at    = excluded.stored_at`,
      /**
       * THE TAGGED CODEC, not `JSON.stringify`.
       *
       * A menu snapshot is full of bigints — every price — and `JSON.stringify`
       * throws on one outright. A cache that cannot hold money would be a trap
       * on a till.
       */
      [kind, encodePayload(payload), version, at.getTime()],
    );
  }

  /**
   * What is held, or null.
   *
   * NO EXPIRY, deliberately, and it is the opposite call from the credential
   * store's. A stale credential is somebody who left still able to open a
   * drawer; a stale menu is last week's prices, which is what a till has always
   * shown during an outage and is strictly better than no menu at all. The
   * version comes back with it so a caller can say how old it is.
   */
  async get<T>(kind: CachedKind): Promise<{ payload: T; version: number } | null> {
    const rows = await this.#driver.all<Row>(
      `SELECT payload_json, version FROM offline_cache WHERE kind = ?`,
      [kind],
    );
    const row = rows[0];
    if (!row) return null;

    try {
      return { payload: decodePayload<T>(row.payload_json), version: row.version };
    } catch {
      // Half-written by a crash mid-transaction. An unreadable cache is an
      // empty one; the alternative is a till that cannot start.
      return null;
    }
  }
}
